/**
 * Audio playback hook for listening module.
 *
 * Manages automatic audio playback for section title and context audios.
 * Handles sequential playback, error handling, and audio state tracking.
 *
 * @module useAudioPlayback
 */

import { useState, useEffect, useRef } from 'react';
import { logger } from '@/lib/logger';
import { buildMediaSource } from '@/utils/examSimulation';

/**
 * Audio item in the queue
 */
export interface AudioQueueItem {
  type: 'section-title' | 'context';
  url?: string | null;
  binary?: string | null;
  mimeType?: string | null;
  contextId?: string;
}

/**
 * Options for audio playback hook
 */
interface UseAudioPlaybackOptions {
  /** Audio queue for current section */
  audioQueue: AudioQueueItem[];
  /** Whether section title audio should be playing */
  isPlayingSectionTitleAudio: boolean;
  /** Whether section title audio has completed */
  sectionTitleAudioCompleted: boolean;
  /** Current context audio index */
  currentContextAudioIndex: number;
  /** Set of completed context audio indices */
  completedContextAudioIndices: Set<number>;
  /** Whether we're in review period (should stop all audio) */
  isReviewPeriod: boolean;
  /** Callback when section title audio completes */
  onSectionTitleAudioComplete: () => void;
  /** Callback when context audio completes */
  onContextAudioComplete: (audioIndex: number) => void;
}

/**
 * Hook for managing audio playback in listening module.
 *
 * Automatically plays section title audio and context audios sequentially.
 * Tracks playing state and handles cleanup.
 *
 * @param options - Audio playback configuration
 * @returns Object containing audio playing state
 */
export function useAudioPlayback({
  audioQueue,
  isPlayingSectionTitleAudio,
  sectionTitleAudioCompleted,
  currentContextAudioIndex,
  completedContextAudioIndices,
  isReviewPeriod,
  onSectionTitleAudioComplete,
  onContextAudioComplete,
}: UseAudioPlaybackOptions) {
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  // The element currently playing, exposed so a waveform can analyse real audio
  const [activeAudioElement, setActiveAudioElement] = useState<HTMLAudioElement | null>(null);

  // Audio refs for auto-playback
  const sectionTitleAudioRef = useRef<HTMLAudioElement | null>(null);
  const contextAudioRefs = useRef<Map<number, HTMLAudioElement>>(new Map());

  /**
   * Auto-play section title audio when section loads
   */
  useEffect(() => {
    if (!isPlayingSectionTitleAudio || isReviewPeriod) {
      return;
    }

    const sectionTitleAudio = audioQueue.find((audio) => audio.type === 'section-title');
    if (!sectionTitleAudio) {
      // No section title audio, mark as completed
      onSectionTitleAudioComplete();
      return;
    }

    const audioSrc = buildMediaSource(
      sectionTitleAudio.url,
      sectionTitleAudio.binary,
      sectionTitleAudio.mimeType,
      'audio/mpeg',
    );

    if (!audioSrc) {
      logger.warn('No audio source available for section title audio');
      onSectionTitleAudioComplete();
      return;
    }

    // Create and play audio
    const audio = new Audio(audioSrc);
    sectionTitleAudioRef.current = audio;
    audio.preload = 'auto';
    audio.volume = 1;

    const handleEnded = () => {
      logger.info('Section title audio completed');
      setIsAudioPlaying(false);
      onSectionTitleAudioComplete();
    };

    const handleError = (event: Event) => {
      const error = event.target as HTMLAudioElement;
      logger.error('Section title audio error', error.error);
      onSectionTitleAudioComplete();
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Auto-play
    audio.play().catch((error) => {
      logger.error('Failed to play section title audio', error);
      onSectionTitleAudioComplete();
    });

    // Track audio playing state for waveform
    const handlePlay = () => {
      setIsAudioPlaying(true);
    };

    const handlePause = () => {
      setIsAudioPlaying(false);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.pause();
      audio.src = '';
      setIsAudioPlaying(false);
    };
  }, [isPlayingSectionTitleAudio, audioQueue, onSectionTitleAudioComplete, isReviewPeriod]);

  /**
   * Auto-play context audios sequentially after section title audio completes
   */
  useEffect(() => {
    if (!sectionTitleAudioCompleted || isReviewPeriod) {
      return;
    }

    // Get context audios from queue
    const contextAudios = audioQueue.filter((audio) => audio.type === 'context');
    if (contextAudios.length === 0) {
      // No context audios, mark as completed
      return;
    }

    // Find the current context audio to play
    if (currentContextAudioIndex >= contextAudios.length) {
      return;
    }

    const currentAudio = contextAudios[currentContextAudioIndex];
    if (completedContextAudioIndices.has(currentContextAudioIndex)) {
      // Already completed, skip
      return;
    }

    const audioSrc = buildMediaSource(
      currentAudio.url,
      currentAudio.binary,
      currentAudio.mimeType,
      'audio/mpeg',
    );

    if (!audioSrc) {
      logger.warn(`No audio source available for context audio ${currentContextAudioIndex}`);
      onContextAudioComplete(currentContextAudioIndex);
      return;
    }

    // Create and play audio
    const audio = new Audio(audioSrc);
    contextAudioRefs.current.set(currentContextAudioIndex, audio);
    audio.preload = 'auto';
    audio.volume = 1;

    const handleEnded = () => {
      logger.info(`Context audio ${currentContextAudioIndex} completed`);
      // Check if any other audio is still playing
      const anyAudioPlaying = 
        (sectionTitleAudioRef.current && !sectionTitleAudioRef.current.paused) ||
        Array.from(contextAudioRefs.current.values()).some(a => a !== audio && !a.paused);
      setIsAudioPlaying(anyAudioPlaying);
      onContextAudioComplete(currentContextAudioIndex);
    };

    const handleError = (event: Event) => {
      const error = event.target as HTMLAudioElement;
      logger.error(`Context audio ${currentContextAudioIndex} error`, error.error);
      onContextAudioComplete(currentContextAudioIndex);
    };

    // Track audio playing state for waveform
    const handlePlay = () => {
      setIsAudioPlaying(true);
    };

    const handlePause = () => {
      setIsAudioPlaying(false);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    // Auto-play
    audio.play().catch((error) => {
      logger.error(`Failed to play context audio ${currentContextAudioIndex}`, error);
      onContextAudioComplete(currentContextAudioIndex);
    });

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.pause();
      audio.src = '';
      contextAudioRefs.current.delete(currentContextAudioIndex);
      // Check if any other audio is still playing
      const anyAudioPlaying = 
        (sectionTitleAudioRef.current && !sectionTitleAudioRef.current.paused) ||
        Array.from(contextAudioRefs.current.values()).some(audio => !audio.paused);
      setIsAudioPlaying(anyAudioPlaying);
    };
  }, [
    sectionTitleAudioCompleted,
    currentContextAudioIndex,
    completedContextAudioIndices,
    audioQueue,
    onContextAudioComplete,
    isReviewPeriod,
  ]);

  /**
   * Stop any ongoing audio when review period starts
   */
  useEffect(() => {
    if (!isReviewPeriod) {
      return;
    }

    if (sectionTitleAudioRef.current) {
      sectionTitleAudioRef.current.pause();
    }
    contextAudioRefs.current.forEach((audio) => audio.pause());
    setIsAudioPlaying(false);
    setActiveAudioElement(null);
  }, [isReviewPeriod]);

  /**
   * Monitor audio playing state to keep waveform in sync
   */
  useEffect(() => {
    const checkAudioState = () => {
      const sectionEl = sectionTitleAudioRef.current;
      let playingEl: HTMLAudioElement | null =
        sectionEl && !sectionEl.paused ? sectionEl : null;
      if (!playingEl) {
        for (const audio of contextAudioRefs.current.values()) {
          if (!audio.paused) {
            playingEl = audio;
            break;
          }
        }
      }
      setIsAudioPlaying(!!playingEl);
      // React bails out when the reference is unchanged, so this only re-renders
      // when the active element actually changes.
      setActiveAudioElement(playingEl);
    };

    // Check periodically to catch any state changes
    const interval = setInterval(checkAudioState, 100);

    return () => clearInterval(interval);
  }, [isPlayingSectionTitleAudio, sectionTitleAudioCompleted, currentContextAudioIndex]);

  return {
    isAudioPlaying,
    activeAudioElement,
  };
}

